import React from "react";
import { IconX } from "../icons/IconX";
import "./NotificationBell.css";

type PendingConnection = {
  subjectListingId: string;
  objectListingId: string;
  roleType: string;
  subjectListingName: string;
  objectListingName: string;
  subjectListingImage: string | null;
  objectListingImage: string | null;
  approvalSide: "subject" | "object";
};

type NotificationPanelProps = {
  pendingConnections: PendingConnection[];
  loading: boolean;
  onClose: () => void;
  onRespond: (
    subjectListingId: string,
    objectListingId: string,
    roleType: string,
    action: "approve" | "reject"
  ) => void;
  isMobileView: boolean;
};

const roleTypeLabels: Record<string, string> = {
  creator: "creator",
  collaborator: "collaborator",
  participant: "participant",
};

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  pendingConnections,
  loading,
  onClose,
  onRespond,
  isMobileView,
}) => {
  const formatConnectionMessage = (conn: PendingConnection) => {
    const roleLabel = roleTypeLabels[conn.roleType] || conn.roleType;

    if (conn.approvalSide === "subject") {
      return (
        <>
          You're invited to be a <strong>{roleLabel}</strong> of{" "}
          <strong>{conn.objectListingName}</strong>
        </>
      );
    } else {
      return (
        <>
          <strong>{conn.subjectListingName}</strong> wants to be a{" "}
          <strong>{roleLabel}</strong> of <strong>{conn.objectListingName}</strong>
        </>
      );
    }
  };

  const panelContent = (
    <>
      <div className="notification-panel-header">
        <h3>Notifications</h3>
        <button className="notification-close-button" onClick={onClose} aria-label="Close">
          <IconX />
        </button>
      </div>

      <div className="notification-panel-content">
        {loading ? (
          <div className="notification-empty">Loading...</div>
        ) : pendingConnections.length === 0 ? (
          <div className="notification-empty">No pending requests</div>
        ) : (
          <ul className="notification-list">
            {pendingConnections.map((conn) => (
              <li
                key={`${conn.subjectListingId}-${conn.objectListingId}-${conn.roleType}`}
                className="notification-item"
              >
                <div className="notification-item-content">
                  <div className="notification-item-image">
                    {conn.subjectListingImage ? (
                      <img src={conn.subjectListingImage} alt="" />
                    ) : (
                      <div className="notification-placeholder-image" />
                    )}
                  </div>
                  <div className="notification-item-text">
                    <p className="notification-message">{formatConnectionMessage(conn)}</p>
                  </div>
                </div>
                <div className="notification-item-actions">
                  <button
                    className="notification-action-button notification-reject"
                    onClick={() =>
                      onRespond(conn.subjectListingId, conn.objectListingId, conn.roleType, "reject")
                    }
                  >
                    Decline
                  </button>
                  <button
                    className="notification-action-button notification-approve"
                    onClick={() =>
                      onRespond(conn.subjectListingId, conn.objectListingId, conn.roleType, "approve")
                    }
                  >
                    Accept
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );

  if (isMobileView) {
    return (
      <div className="notification-panel-overlay">
        <div className="notification-panel notification-panel-fullscreen">{panelContent}</div>
      </div>
    );
  }

  return <div className="notification-panel notification-panel-dropdown">{panelContent}</div>;
};
